import React, { useState, useEffect } from 'react';
import './App.css';
import { auth, db, functions } from './firebase'; // Assuming firebase.js is in the same src folder
import { GoogleAuthProvider, signInWithPopup, onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions"; // Import for calling Strava functions
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react'; // Add this import
import StravaAuthCallbackPage from './StravaAuthCallbackPage'; // New callback for custom auth
import RequestGeneratorPage from './RequestGeneratorPage'; // Import the new page component
import SharePage from './SharePage'; // Import the new SharePage component
import IOSDownloadPage from './IOSDownloadPage'; // Import the iOS download page
import NavigationBar from './NavigationBar'; // Import Navbar
import LandingPage from './LandingPage'; // Assuming LandingPage is refactored from App
import HistoryPage from './HistoryPage'; // Import new HistoryPage
import PromptManagementPage from './PromptManagementPage'; // Import the new page
import StravaWebhookAdminPage from './StravaWebhookAdminPage'; // Import Strava Webhook Admin page
import TargettedActivityGeneration from './TargettedActivityGeneration'; // Add this import
import AdminDashboard from './AdminDashboard'; // Import Admin Dashboard
import TestComparisonPage from './TestComparisonPage'; // Import Test Comparison Page
import TestComparisonsListPage from './TestComparisonsListPage'; // Import Test Comparisons List Page
import Footer from './Footer'; // Import Footer component
import PrivacyPolicyPage from './PrivacyPolicyPage'; // Import legal pages
import CookiePolicyPage from './CookiePolicyPage';
import TermsOfServicePage from './TermsOfServicePage';
import SettingsPage from './SettingsPage'; // Add this import
import CreditsPage from './CreditsPage'; // Add this import
import RequestBasedGenerationPage from './RequestBasedGenerationPage'; // Add this import
import GalleryPage from './GalleryPage'; // Add this import
import BadgesAdminPage from './BadgesAdminPage'; // Add this import
import UserBadgesPage from './UserBadgesPage';
import CouponPage from './CouponPage'; // Add coupon page import
import CreatePage from './CreatePage'; // Multi-activity map creation
import AutoUploadClaimPage from './AutoUploadClaimPage';
import { AdminViewProvider } from './contexts/AdminViewContext'; // Add admin view context

// Strava OAuth Configuration - Client ID is safe to expose, Client Secret is NOT.
// Ensure this matches your Strava App Client ID and the one in your Cloud Functions config
// const STRAVA_CLIENT_ID = "159733"; // Your actual Strava Client ID
// const STRAVA_REDIRECT_URI = window.location.origin + "/strava-callback"; // New React route
// const STRAVA_SCOPE = "activity:read_all";

// Component to handle page view tracking
function PageViewTracker() {
  const location = useLocation();
  const posthog = usePostHog();

  useEffect(() => {
    if (posthog) {
      // Create more descriptive page names
      const getPageName = (pathname) => {
        if (pathname === '/') return 'Landing Page';
        if (pathname === '/history') return 'User History';
        if (pathname === '/settings') return 'User Settings';
        if (pathname === '/credits') return 'Credits Management';
        if (pathname === '/gallery') return 'Gallery';
        if (pathname === '/generate') return 'Map Generator';
        if (pathname.startsWith('/generate/activity/')) return 'Activity-Specific Generator';
        if (pathname.startsWith('/generate/strava/')) return 'Strava Activity Generator';
        if (pathname.startsWith('/generate/request/')) return 'Admin Request-Based Generator';
        if (pathname.startsWith('/map/')) return 'Map Share Page';
        if (pathname.startsWith('/ios-download/')) return 'iOS Download Page';
        if (pathname === '/strava-auth-callback') return 'Strava Auth Callback';
        if (pathname === '/admin/test-comparisons') return 'Admin Test Comparisons List';
        if (pathname.startsWith('/admin/compare/')) return 'Admin Test Comparison';
        if (pathname === '/admin/prompts') return 'Admin Prompt Management';
        if (pathname === '/admin/strava-webhook') return 'Admin Strava Webhook';
        if (pathname === '/admin/badges') return 'Admin Badges Management';
        if (pathname === '/privacy-policy') return 'Privacy Policy';
        if (pathname === '/cookie-policy') return 'Cookie Policy';
        if (pathname === '/terms-of-service') return 'Terms of Service';
        return pathname;
      };

      posthog.capture('page_viewed', {
        page_name: getPageName(location.pathname),
        pathname: location.pathname,
        search: location.search,
        page_url: window.location.href,
      });
    }
  }, [location, posthog]);

  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true); // To prevent brief flash of signed-out content
  const [showNavigation, setShowNavigation] = useState(true); // New state for navigation visibility
  const posthog = usePostHog();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setLoadingAuth(false);
      
      // Identify user in PostHog
      if (currentUser && posthog) {
        // Get additional user data from Firestore if available
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          const userData = userDoc.exists() ? userDoc.data() : {};
          
          posthog.identify(currentUser.uid, {
            email: currentUser.email,
            name: currentUser.displayName,
            // Add any additional properties from Firestore
            strava_connected: userData.stravaConnected || false,
            signup_date: userData.createdAt?.toDate?.() || currentUser.metadata.creationTime,
            last_login: new Date(),
            // Add any other relevant user properties
          });
        } catch (error) {
          // Fallback to basic identification if Firestore fails
          posthog.identify(currentUser.uid, {
            email: currentUser.email,
            name: currentUser.displayName,
            last_login: new Date(),
          });
        }
      } else if (posthog) {
        posthog.reset(); // Clear user data when signed out
      }
    });
    return () => unsubscribe();
  }, [posthog]);

  if (loadingAuth) {
    return <div style={{ textAlign: 'center', marginTop: '50px' }}>Loading application...</div>;
  }

  return (
    <AdminViewProvider>
      <Router>
        <div className="app-wrapper">
          <PageViewTracker />
          {showNavigation && <NavigationBar user={user} />} {/* Conditionally render NavigationBar */}
          <main className={`app-content-container ${showNavigation ? 'with-navigation' : ''}`}> {/* Add class for CSS targeting */}
            <Routes>
              <Route path="/" element={<LandingPage user={user} setShowNavigation={setShowNavigation} />} /> {/* Pass setShowNavigation */}
              <Route path="/strava-auth-callback" element={<StravaAuthCallbackPage />} /> {/* New route */}
              <Route path="/generate/strava/:stravaId" element={<RequestGeneratorPage />} />
              <Route path="/generate" element={<RequestGeneratorPage />} />
              <Route path="/generate/activity/:stravaId" element={<TargettedActivityGeneration user={user} />} /> {/* Add this route */}
              <Route path="/generate/request/:requestId" element={<RequestBasedGenerationPage user={user} />} /> {/* Admin-only route for regenerating from request */}
              <Route path="/history" element={<HistoryPage user={user} />} /> {/* New route, pass user */} 
              <Route path="/settings" element={<SettingsPage user={user} />} /> {/* Add this new route */}
              <Route path="/credits" element={<CreditsPage user={user} />} /> {/* Add this new route */}
              <Route path="/coupon" element={<CouponPage user={user} />} /> {/* Coupon redemption page */}
              <Route path="/badges" element={<UserBadgesPage user={user} />} /> {/* User badges page */}
              <Route path="/gallery" element={<GalleryPage />} /> {/* Add gallery route */}
              <Route path="/create" element={<CreatePage user={user} />} /> {/* Multi-activity map creation */}
              {/* Add a /generate route for new requests without a Strava ID from URL */}
              <Route path="/map/:requestId" element={<SharePage />} />
              <Route path="/auto-upload/:token" element={<AutoUploadClaimPage user={user} />} />
              <Route path="/ios-download/:requestId" element={<IOSDownloadPage />} /> {/* New route for iOS download experience */}
              <Route path="/admin/dashboard" element={<AdminDashboard user={user} />} /> {/* Admin Dashboard */}
              <Route path="/admin/test-comparisons" element={<TestComparisonsListPage user={user} />} /> {/* Test Comparisons List */}
              <Route path="/admin/compare/:parentId" element={<TestComparisonPage user={user} />} /> {/* Test Comparison Page */}
              <Route path="/admin/prompts" element={<PromptManagementPage user={user} />} /> {/* New route for prompt management */}
              <Route path="/admin/strava-webhook" element={<StravaWebhookAdminPage user={user} />} /> {/* Strava Webhook Admin */}
              <Route path="/admin/badges" element={<BadgesAdminPage user={user} />} /> {/* Badges Admin */}
              {/* Legal pages */}
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/cookie-policy" element={<CookiePolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            </Routes>
          </main>
          <Footer /> {/* Add Footer to all pages */}
        </div>
      </Router>
    </AdminViewProvider>
  );
}

export default App;
